// Bildzeichen der Branchen (Ebene 1) für die FORMULARFELDER — auf Basis von
// @fluentui/react-icons (PO 09.09.2026, Anforderung 6: „Icons, die die Branche
// darstellen", vor jedem Eintrag des Branche-Dropdowns und vor dem gewählten
// Wert im geschlossenen Feld).
//
// WARUM EIN EIGENES MODUL neben `brancheIcons.tsx`: Jenes liefert bewusst ROHE
// SVG-Fragmente ohne <svg>-Hülle im 36×36-Raster, weil dieselben Zeichen auch
// INNERHALB des Organigramm-SVG per `<svg x y width height>` positioniert
// werden. Ein fertiges Fluent-<svg> mit eigenem viewBox ist dort nicht
// positionierbar — die beiden Formen dürfen deshalb nicht in denselben Record.
// `brancheIcons.tsx` bleibt unangetastet und bedient weiter Risiken-Reiter und
// Organigramm.
//
// FOLGE, DIE JEMAND ENTSCHEIDEN MUSS: Dieselbe Branche trägt damit in der
// Eingabemaske ein Fluent-Zeichen und im Risiken-Reiter/Organigramm das
// gezeichnete SVG. Wer beide Bildsprachen zusammenführen will, ersetzt die
// Zeichen an EINER Stelle — nicht durch ein drittes Modul.
//
// WARUM `*20Regular`: Die im Repo mit Abstand häufigste Größe/Variante. Alle
// Namen sind gegen die installierte Fassung geprüft (@fluentui/react-icons
// 2.0.327, Range ^2.0.245); ein erfundener Name bräche erst im Build.

import {
  Briefcase20Regular,
  BuildingBank20Regular,
  BuildingFactory20Regular,
  BuildingGovernment20Regular,
  BuildingMultiple20Regular,
  type FluentIcon,
  HeartPulse20Regular,
  Laptop20Regular,
  LeafThree20Regular,
  MoreHorizontal20Regular,
  PeopleCommunity20Regular,
  ShoppingBag20Regular,
  Tag20Regular,
  VehicleTruck20Regular,
  Wrench20Regular,
} from "@fluentui/react-icons";
import { BRANCHE_AUFFANG_EBENE1, BRANCHE_AUFFANG_EBENE1_ALT } from "./brancheGruppen";

/**
 * Branche (Ebene 1) → Fluent-Icon-Komponente. Die Schlüssel sind die 13
 * Ebene-1-Namen des Zwei-Ebenen-Katalogs
 * (`apps/api/src/data/branchengruppen.ts`).
 *
 * Die Zeichen sind so gewählt, dass ein Versicherungsmakler sie ohne Erklärung
 * wiedererkennt: Werkzeug für das Handwerk, Fabrik für die Produktion, Bank für
 * die Finanzwirtschaft, LKW für die Logistik.
 */
export const BRANCHE_FLUENT_ICONS: Record<string, FluentIcon> = {
  "Bau & Handwerk": Wrench20Regular,
  "Dienstleistung & Freie Berufe": Briefcase20Regular,
  "Energie & Umwelt": LeafThree20Regular,
  Finanzwirtschaft: BuildingBank20Regular,
  Gesundheitswirtschaft: HeartPulse20Regular,
  "Handel & Konsum": ShoppingBag20Regular,
  "IT & Digitalwirtschaft": Laptop20Regular,
  Immobilienwirtschaft: BuildingMultiple20Regular,
  "Industrie & Produktion": BuildingFactory20Regular,
  "Öffentliche Hand, Bildung & Forschung": BuildingGovernment20Regular,
  "Transport & Logistik": VehicleTruck20Regular,
  // Die echte Auffangbranche (Landwirtschaft, Sport, Kultur, Brauchtum,
  // Gesellschaft) — sie steht für Vereine und Gemeinschaften.
  [BRANCHE_AUFFANG_EBENE1]: PeopleCommunity20Regular,
  // Die künstliche Auffangbranche wird nicht mehr angeboten, kommt aber im
  // Bestand vor. Ohne eigenen Schlüssel wäre sie vom Auffangzeichen für
  // UNBEKANNTE Branchen nicht zu unterscheiden.
  [BRANCHE_AUFFANG_EBENE1_ALT]: MoreHorizontal20Regular,
};

/**
 * Auffangzeichen für eine Branche ohne eigenes Bild.
 *
 * WOFÜR: Der Katalog wird aus einer Excel erzeugt und wächst. Fällt später eine
 * Ebene-1-Branche dazu, darf das Dropdown weder leer laufen noch abstürzen —
 * es zeigt dann dieses Zeichen, bis jemand ein eigenes pflegt.
 */
export const BRANCHE_FLUENT_FALLBACK: FluentIcon = Tag20Regular;

/** Zeichen einer Ebene-1-Branche; unbekannte Bezeichnung → Auffangzeichen. */
export const brancheFluentIcon = (ebene1: string | null | undefined): FluentIcon =>
  (ebene1 && BRANCHE_FLUENT_ICONS[ebene1.trim()]) || BRANCHE_FLUENT_FALLBACK;
